import { Injectable } from '@angular/core';
import {
  CommonToggleService,
  StorageUtils,
  ToggleContextProvider,
  Utils,
  WallboxConnectionType,
} from '@kw-shared/common';
import {
  CertificateVerificationMode,
  CommunicationChannel,
  WallboxConfiguration,
  WallboxType,
} from 'apps/keba-wallbox-app/src/plugins/keba-wallbox-plugin';
import { LocalWallboxConnectionPortDefinition } from '../definitions/local-wallbox-connection-port.definition';

export abstract class IWallboxConfig {
  _class: string;

  constructor(wallbox?: IWallboxConfig) {
    Object.assign(this, wallbox);
  }

  abstract get connectionType(): WallboxConnectionType;

  isLocalWallbox(): this is LocalWallboxConfig {
    return (
      this.connectionType === WallboxConnectionType.LOCAL_REST ||
      this.connectionType === WallboxConnectionType.LOCAL_UDP ||
      this.connectionType === WallboxConnectionType.LOCAL_BLE
    );
  }

  isRemoteWallbox(): this is RemoteWallboxConfig {
    return this.connectionType === WallboxConnectionType.REMOTE;
  }
}

export class LocalWallboxConfig extends IWallboxConfig {
  override _class = 'local-wallbox-config';

  address: string;
  channel: CommunicationChannel;
  serialNumber?: string;
  password?: string;
  apiVersion?: string;
  verificationMode?: CertificateVerificationMode;
  productCode?: string;
  type?: string;

  public get connectionType(): WallboxConnectionType {
    switch (this.channel) {
      case CommunicationChannel.UDP:
        return WallboxConnectionType.LOCAL_UDP;
      case CommunicationChannel.REST:
        return WallboxConnectionType.LOCAL_REST;
      case CommunicationChannel.BLE:
        return WallboxConnectionType.LOCAL_BLE;
    }
  }

  public toWallboxConfiguration(): WallboxConfiguration {
    let port: number | undefined;
    switch (this.channel) {
      case CommunicationChannel.UDP:
        port = LocalWallboxConnectionPortDefinition.UDP;
        break;
      case CommunicationChannel.REST:
        port = LocalWallboxConnectionPortDefinition.REST;
        break;
      case CommunicationChannel.BLE: {
        port = undefined;
        break;
      }
    }

    return {
      ...this,
      port,
    } as WallboxConfiguration;
  }
}

export class RemoteWallboxConfig extends IWallboxConfig {
  override _class = 'remote-wallbox-config';
  id: string;
  masterChargePointId?: string;
  hasClients: boolean;
  clientChargePoints?: string[];
  serialNumber?: string;
  connectorId?: number;

  public get connectionType(): WallboxConnectionType {
    return WallboxConnectionType.REMOTE;
  }

  public get isClientWallbox(): boolean {
    return this.masterChargePointId !== null;
  }
}

@Injectable({
  providedIn: 'root',
})
export class CurrentWallboxConfigService {
  private static readonly WALLBOX_STORAGE_KEY = 'kwa-current-wallbox-config';

  private currentWallboxConfig?: IWallboxConfig;
  private readonly contextProvider: ToggleContextProvider;

  constructor(private readonly toggleService: CommonToggleService) {
    this.contextProvider = ToggleContextProvider.instance;
  }

  public getCurrentWallboxConfig(): IWallboxConfig | undefined {
    if (!this.currentWallboxConfig) {
      this.currentWallboxConfig = this.loadSavedCurrentWallboxConfig();
    }
    this.updateToggleContext();
    return this.currentWallboxConfig;
  }

  private updateToggleContext() {
    if (this.currentWallboxConfig) {
      let isLocalWallbox = this.currentWallboxConfig.isLocalWallbox();
      if (isLocalWallbox) {
        const localConfig = this.currentWallboxConfig as LocalWallboxConfig;
        this.contextProvider.updateContext({
          connectionType: localConfig.connectionType,
          restApiVersion: localConfig.apiVersion,
          type: Utils.isValidEnumValue(WallboxType, localConfig.type) ? (localConfig.type as WallboxType) : undefined,
        });
      } else {
        this.contextProvider.updateContext({ connectionType: WallboxConnectionType.REMOTE, restApiVersion: undefined });
      }
    }
  }

  public setCurrentWallboxConfig(config?: IWallboxConfig): void {
    this.currentWallboxConfig = config;
    this.contextProvider.updateContext({ connectionType: this.currentWallboxConfig?.connectionType });
    StorageUtils.set(CurrentWallboxConfigService.WALLBOX_STORAGE_KEY, this.currentWallboxConfig);
  }

  private loadSavedCurrentWallboxConfig(): IWallboxConfig | undefined {
    const config = StorageUtils.get<IWallboxConfig>(CurrentWallboxConfigService.WALLBOX_STORAGE_KEY);

    if (config) {
      return config._class === new RemoteWallboxConfig()._class
        ? new RemoteWallboxConfig(config)
        : new LocalWallboxConfig(config);
    }
    return undefined;
  }

  public isM20(config: any): boolean {
    if (!config) {
      return false;
    }
    return (
      (config.hasOwnProperty('type') && config['type'] === WallboxType.M20) ||
      (config.hasOwnProperty('productCode') &&
        config['productCode'] != undefined &&
        config['productCode'].startsWith('KC-M20-')) ||
      this.toggleService.isM20()
    );
  }
}
